/**
 * Transaction Actions Utility
 *
 * Provides logic for determining what actions can be taken on transactions
 * based on fiscal period status, transaction status, and user permissions.
 *
 * Per Implementation Plan Phase 4: Void vs Delete Guardrails
 */

import {
  Invoice,
  Bill,
  JournalEntry,
  Payment,
  InvoiceStatus,
  BillStatus,
  PaymentStatus,
} from "../../types";
import {
  canVoidTransactions,
  canAdjustClosedPeriod,
  AuthorizableUser,
} from "./authorization";

/**
 * Fiscal Period status
 */
export type FiscalPeriodStatus = "open" | "closed" | "locked";

/**
 * Fiscal Period interface
 */
export interface FiscalPeriod {
  id: string;
  companyId: string;
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  status: FiscalPeriodStatus;
  closedBy?: string;
  closedAt?: string;
  closeNotes?: string;
  lastReopenedBy?: string;
  lastReopenedAt?: string;
  reopenReason?: string;
}

/**
 * Transaction type for action checks
 */
export type TransactionType = "invoice" | "bill" | "journal_entry" | "payment";

/**
 * Actions available for a transaction
 */
export interface TransactionActions {
  canEdit: boolean;
  canDelete: boolean;
  canVoid: boolean;
  canPost?: boolean; // For journal entries
  canReverse?: boolean; // For posted journal entries
  restrictions: TransactionRestriction[];
}

/**
 * Restriction reason with details
 */
export interface TransactionRestriction {
  action: "edit" | "delete" | "void" | "post" | "reverse";
  reason: string;
  severity: "warning" | "error";
}

/**
 * Transaction status values for normalization
 */
type NormalizedStatus =
  | "draft"
  | "pending"
  | "posted"
  | "paid"
  | "voided"
  | "cancelled"
  | "reversed";

/**
 * Normalize transaction status to a common set
 */
function normalizeStatus(
  status: InvoiceStatus | BillStatus | PaymentStatus | string
): NormalizedStatus {
  const statusMap: Record<string, NormalizedStatus> = {
    draft: "draft",
    pending: "pending",
    sent: "pending",
    received: "pending",
    posted: "posted",
    paid: "paid",
    completed: "paid",
    voided: "voided",
    cancelled: "cancelled",
    overdue: "pending",
    reversed: "reversed",
    failed: "cancelled",
  };
  return statusMap[status.toLowerCase()] || "pending";
}

/**
 * Check if a date falls within a fiscal period
 */
export function isInFiscalPeriod(date: string, period: FiscalPeriod): boolean {
  const txnDate = new Date(date);
  const startDate = new Date(period.startDate);
  const endDate = new Date(period.endDate);
  return txnDate >= startDate && txnDate <= endDate;
}

/**
 * Get the fiscal period for a transaction date
 */
export function getFiscalPeriodForDate(
  date: string,
  periods: FiscalPeriod[]
): FiscalPeriod | null {
  return periods.find((p) => isInFiscalPeriod(date, p)) || null;
}

/**
 * Check if transaction is in current month
 */
export function isInCurrentMonth(date: string): boolean {
  const txnDate = new Date(date);
  const now = new Date();
  return (
    txnDate.getFullYear() === now.getFullYear() &&
    txnDate.getMonth() === now.getMonth()
  );
}

/**
 * Get transaction actions for an Invoice
 */
export function getInvoiceActions(
  invoice: Invoice,
  fiscalPeriod: FiscalPeriod | null,
  user: AuthorizableUser | null
): TransactionActions {
  const restrictions: TransactionRestriction[] = [];
  const status = normalizeStatus(invoice.status);

  // Check edit permissions
  let canEdit = status === "draft";
  if (!canEdit && status !== "draft") {
    restrictions.push({
      action: "edit",
      reason: "Only draft invoices can be edited",
      severity: "warning",
    });
  }

  // Check delete permissions - only draft invoices can be deleted
  let canDelete = status === "draft";
  if (!canDelete) {
    restrictions.push({
      action: "delete",
      reason: "Only draft invoices can be deleted. Use void for sent invoices.",
      severity: "error",
    });
  }

  // Check void permissions
  let canVoid = status === "pending" || status === "paid";
  if (status === "voided" || status === "cancelled") {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "Invoice is already voided or cancelled",
      severity: "warning",
    });
  }

  // Check fiscal period restrictions
  if (fiscalPeriod) {
    if (fiscalPeriod.status === "locked") {
      canEdit = false;
      canDelete = false;
      canVoid = false;
      restrictions.push({
        action: "edit",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
      restrictions.push({
        action: "void",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
    } else if (fiscalPeriod.status === "closed") {
      // Only authorized users can modify closed periods
      if (!canAdjustClosedPeriod(user)) {
        canEdit = false;
        canVoid = false;
        restrictions.push({
          action: "edit",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
        restrictions.push({
          action: "void",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
      } else {
        restrictions.push({
          action: "void",
          reason: `Warning: Modifying closed period ${fiscalPeriod.month}/${fiscalPeriod.year}`,
          severity: "warning",
        });
      }
    }
  }

  // Check user void permissions
  if (canVoid && !canVoidTransactions(user)) {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "You do not have permission to void invoices",
      severity: "error",
    });
  }

  return { canEdit, canDelete, canVoid, restrictions };
}

/**
 * Get transaction actions for a Bill
 */
export function getBillActions(
  bill: Bill,
  fiscalPeriod: FiscalPeriod | null,
  user: AuthorizableUser | null
): TransactionActions {
  const restrictions: TransactionRestriction[] = [];
  const status = normalizeStatus(bill.status);

  // Check edit permissions
  let canEdit = status === "draft" || status === "pending";
  if (!canEdit) {
    restrictions.push({
      action: "edit",
      reason: "Only draft or received bills can be edited",
      severity: "warning",
    });
  }

  // Check delete permissions - only draft bills can be deleted
  let canDelete = status === "draft";
  if (!canDelete) {
    restrictions.push({
      action: "delete",
      reason: "Only draft bills can be deleted. Use void for received bills.",
      severity: "error",
    });
  }

  // Check void permissions
  let canVoid = status === "pending" || status === "paid";
  if (status === "voided" || status === "cancelled") {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "Bill is already voided or cancelled",
      severity: "warning",
    });
  }

  // Check fiscal period restrictions
  if (fiscalPeriod) {
    if (fiscalPeriod.status === "locked") {
      canEdit = false;
      canDelete = false;
      canVoid = false;
      restrictions.push({
        action: "edit",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
      restrictions.push({
        action: "void",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
    } else if (fiscalPeriod.status === "closed") {
      if (!canAdjustClosedPeriod(user)) {
        canEdit = false;
        canVoid = false;
        restrictions.push({
          action: "edit",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
        restrictions.push({
          action: "void",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
      } else {
        restrictions.push({
          action: "void",
          reason: `Warning: Modifying closed period ${fiscalPeriod.month}/${fiscalPeriod.year}`,
          severity: "warning",
        });
      }
    }
  }

  // Check user void permissions
  if (canVoid && !canVoidTransactions(user)) {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "You do not have permission to void bills",
      severity: "error",
    });
  }

  return { canEdit, canDelete, canVoid, restrictions };
}

/**
 * Get transaction actions for a Journal Entry
 */
export function getJournalEntryActions(
  entry: JournalEntry,
  fiscalPeriod: FiscalPeriod | null,
  user: AuthorizableUser | null
): TransactionActions {
  const restrictions: TransactionRestriction[] = [];
  const status = normalizeStatus(entry.status);

  // Check edit permissions - only draft entries can be edited
  let canEdit = status === "draft";
  if (!canEdit) {
    restrictions.push({
      action: "edit",
      reason: "Only draft journal entries can be edited",
      severity: "warning",
    });
  }

  // Check delete permissions - only draft entries can be deleted
  let canDelete = status === "draft";
  if (!canDelete) {
    restrictions.push({
      action: "delete",
      reason:
        "Only draft journal entries can be deleted. Use reverse for posted entries.",
      severity: "error",
    });
  }

  // Check post permissions
  let canPost = status === "draft";
  if (status === "posted" || status === "reversed") {
    canPost = false;
  }

  // Check reverse permissions - only posted entries can be reversed
  let canReverse = status === "posted";
  if (status !== "posted") {
    restrictions.push({
      action: "reverse",
      reason: "Only posted journal entries can be reversed",
      severity: "warning",
    });
  }

  // Journal entries don't have a traditional "void" - they are reversed
  const canVoid = false;

  // Check fiscal period restrictions
  if (fiscalPeriod) {
    if (fiscalPeriod.status === "locked") {
      canEdit = false;
      canDelete = false;
      canPost = false;
      canReverse = false;
      restrictions.push({
        action: "edit",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
      restrictions.push({
        action: "post",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
      restrictions.push({
        action: "reverse",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
    } else if (fiscalPeriod.status === "closed") {
      if (!canAdjustClosedPeriod(user)) {
        canEdit = false;
        canPost = false;
        canReverse = false;
        restrictions.push({
          action: "edit",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
        restrictions.push({
          action: "post",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
        restrictions.push({
          action: "reverse",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
      } else {
        restrictions.push({
          action: "post",
          reason: `Warning: Posting to closed period ${fiscalPeriod.month}/${fiscalPeriod.year}`,
          severity: "warning",
        });
      }
    }
  }

  return { canEdit, canDelete, canVoid, canPost, canReverse, restrictions };
}

/**
 * Get transaction actions for a Payment
 */
export function getPaymentActions(
  payment: Payment,
  fiscalPeriod: FiscalPeriod | null,
  user: AuthorizableUser | null
): TransactionActions {
  const restrictions: TransactionRestriction[] = [];
  const status = normalizeStatus(payment.status);

  // Payments generally can't be edited after creation
  let canEdit = status === "pending";
  if (!canEdit) {
    restrictions.push({
      action: "edit",
      reason: "Only pending payments can be edited",
      severity: "warning",
    });
  }

  // Payments can't be deleted - only voided
  const canDelete = false;
  restrictions.push({
    action: "delete",
    reason: "Payments cannot be deleted. Use void to cancel a payment.",
    severity: "error",
  });

  // Check void permissions
  let canVoid = status === "pending" || status === "paid";
  if (status === "voided" || status === "cancelled") {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "Payment is already voided or cancelled",
      severity: "warning",
    });
  }

  // Check fiscal period restrictions
  if (fiscalPeriod) {
    if (fiscalPeriod.status === "locked") {
      canEdit = false;
      canVoid = false;
      restrictions.push({
        action: "edit",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
      restrictions.push({
        action: "void",
        reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is locked`,
        severity: "error",
      });
    } else if (fiscalPeriod.status === "closed") {
      if (!canAdjustClosedPeriod(user)) {
        canEdit = false;
        canVoid = false;
        restrictions.push({
          action: "edit",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
        restrictions.push({
          action: "void",
          reason: `Fiscal period ${fiscalPeriod.month}/${fiscalPeriod.year} is closed. Contact accounting lead to reopen.`,
          severity: "error",
        });
      } else {
        restrictions.push({
          action: "void",
          reason: `Warning: Modifying closed period ${fiscalPeriod.month}/${fiscalPeriod.year}`,
          severity: "warning",
        });
      }
    }
  }

  // Check user void permissions
  if (canVoid && !canVoidTransactions(user)) {
    canVoid = false;
    restrictions.push({
      action: "void",
      reason: "You do not have permission to void payments",
      severity: "error",
    });
  }

  return { canEdit, canDelete, canVoid, restrictions };
}

/**
 * Get action restrictions as display strings
 */
export function getRestrictionMessages(
  restrictions: TransactionRestriction[],
  action?: "edit" | "delete" | "void" | "post" | "reverse"
): string[] {
  const filtered = action
    ? restrictions.filter((r) => r.action === action)
    : restrictions;
  return filtered.map((r) => r.reason);
}

/**
 * Check if action has any blocking restrictions (errors)
 */
export function hasBlockingRestriction(
  restrictions: TransactionRestriction[],
  action: "edit" | "delete" | "void" | "post" | "reverse"
): boolean {
  return restrictions.some((r) => r.action === action && r.severity === "error");
}

/**
 * Check if action has warnings (non-blocking)
 */
export function hasWarningRestriction(
  restrictions: TransactionRestriction[],
  action: "edit" | "delete" | "void" | "post" | "reverse"
): boolean {
  return restrictions.some(
    (r) => r.action === action && r.severity === "warning"
  );
}
